import type { Knowledge } from '../../services/mekakApi';

// ─── Re-export API types ────────────────────────────────────────────────────

export type {
  Knowledge,
  KnowledgeMetadata,
  KnowledgeCreateRequest,
  SearchRequest,
  SearchResult,
  SQLAnswer,
  LLMAnswer,
  AdvancedSearchResponse,
  QueryRecord,
  DashboardStats,
  CSVFile,
  CSVFileDetail,
  CSVColumnDetail,
  CSVUploadResult,
  FileDocument,
  FileProcessResult,
  FileDocumentDetail,
  KnowledgeImage,
  RelatedImage,
  KnowledgeFolder,
  FolderTreeNode,
  FolderContentsResponse,
} from '../../services/mekakApi';

// ─── UI Constants ───────────────────────────────────────────────────────────

/** AI search — mode label displayed in AnswerCard */
export const queryModeLabel: Record<string, { text: string; color: string }> = {
  pure_sql: { text: 'SQL 精確查詢', color: 'green' },
  mixed: { text: '混合搜尋', color: 'purple' },
};

/** AI search — placeholder per search mode */
export const searchModePlaceholder: Record<string, string> = {
  mixed: '輸入自然語言問題，AI 會自動判斷最佳搜尋方式',
  pure_sql: '輸入資料查詢，例如：找 SANYEI CORPORATION 的項目、價格低於 500 的產品',
};

/** AI search — spin tip per search mode */
export const searchModeSpinTip: Record<string, string> = {
  mixed: 'AI 正在分析您的問題...',
  pure_sql: '正在查詢 ERP 資料庫...',
};

/** CSV — vectorization status display */
export const vecStatusMap: Record<string, { text: string; color: string }> = {
  completed: { text: '已完成', color: 'green' },
  processing: { text: '處理中', color: 'blue' },
  pending: { text: '待處理', color: 'default' },
  failed: { text: '失敗', color: 'red' },
  unknown: { text: '未知', color: 'default' },
};

/** CSV — data type tag colors */
export const dataTypeColor: Record<string, string> = {
  text: 'blue',
  numeric: 'green',
  integer: 'cyan',
  identifier: 'orange',
};

/** Knowledge — tag color palette */
export const TAG_COLORS = [
  'blue', 'geekblue', 'purple', 'cyan', 'teal',
  'green', 'lime', 'gold', 'orange', 'volcano',
];

// ─── Dashboard types ────────────────────────────────────────────────────────

export interface DailyStat {
  date: string;
  queries: number;
  tokens: number;
  cost: number;
}

export interface OverviewCardConfig {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  suffix?: string;
}

// ─── File upload queue ──────────────────────────────────────────────────────

/** Per-file state in the multi-file upload queue.
 * `submitted` = backend accepted the bytes (HTTP 200) and is processing in background.
 * It is NOT the same as fully completed processing — the KnowledgeList view polls for that. */
export type UploadQueueStatus = 'waiting' | 'uploading' | 'submitted' | 'failed';

export interface UploadQueueItem {
  uid: string;
  file: File;
  status: UploadQueueStatus;
  message?: string;
  pageCount?: number;
  knowledgeCount?: number;
  folderID?: string | null;
}

/** Hard cap on simultaneous queued files to protect the browser and backend. */
export const UPLOAD_QUEUE_MAX = 20;

// ─── KnowledgeList types ────────────────────────────────────────────────────

export interface KnowledgeListPageState {
  knowledgeList: Knowledge[];
  loading: boolean;
  search: string;
  selectedTags: string[];
  allTags: string[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
}
